import {
  RTC_ROOM_EVENTS,
  SIGNALING_PROTOCOL,
  RTC_FEATURE_PEER_CONNECTION,
  RTC_FEATURE_AUDIO_CODECS,
  RTC_FEATURE_VIDEO_CODECS,
} from 'app/services/rtc/constants';
import TrackState from 'app/services/rtc/TrackState';
import {
  PeerConnection,
  PeerConnectionEvents,
} from 'app/services/rtc/PeerConnection';
import LimitedPeerConnection from 'app/services/rtc/LimitedPeerConnection';
import { tracePeerConnection } from 'app/services/rtc/rtcstats';
import bindMethods from 'app/utils/bindMethods';
import { bindEvents } from 'app/utils/events';
import FluentdLogger from 'app/services/fluentdLogger';
import Logger from 'app/services/logger';
import { CancelToken } from 'app/utils/cancelToken';

export default class Peer {
  constructor({ room, isBot, id, audioStream, videoStream, screenStream, isOfferer, features }) {
    this.room = room;
    this.id = id;
    this.isBot = isBot;
    this.speaking = false;
    this.channels = {
      audio: (features[RTC_FEATURE_AUDIO_CODECS] || []).length > 0,
      video: (features[RTC_FEATURE_VIDEO_CODECS] || []).length > 0,
    };
    this.canceller = CancelToken.create();

    this.signalingEventMap = bindMethods(this, {
      [SIGNALING_PROTOCOL.updates.mediaStatus]: this.receiveMediaStatus,
      [SIGNALING_PROTOCOL.bot.stateUpdated]: this.receiveBotStateUpdated,
    });

    if (features[RTC_FEATURE_PEER_CONNECTION]) {
      this.initConnection({
        iceServers: [...room.stunservers, ...room.turnservers],
        isOfferer,
        audioStream,
        videoStream,
        screenStream,
        features,
      });
    } else {
      this.connection = new LimitedPeerConnection();
    }
  }

  initConnection({ iceServers, isOfferer, audioStream, videoStream, screenStream }) {
    const tracer = connection => tracePeerConnection(
      connection,
      this.id,
      this.room.stats.bind(this.room),
      {
        getStatsInterval: 5000,
      },
    );

    const peerConnectionEventMap = bindMethods(this, {
      [PeerConnectionEvents.remoteAudioStream]:     this.onRemoteAudioStream,
      [PeerConnectionEvents.remoteVideoStream]:     this.onRemoteVideoStream,
      [PeerConnectionEvents.remoteScreenStream]:    this.onRemoteScreenStream,
      [PeerConnectionEvents.connectionStateChange]: this.onConnectionStateChange,
      [PeerConnectionEvents.connectionError]:       this.onConnectionError,
      [PeerConnectionEvents.connectionTimeout]:     this.onConnectionTimeout,
    });

    this.connection = new PeerConnection({
      id: this.id,
      config: { iceServers },
      channels: this.channels,
      tracer,
      isOfferer,
      send: bindMethods(this, {
        offer:             this.sendOffer,
        negotiationNeeded: this.sendNegotiationNeeded,
        iceCandidate:      this.sendIceCandidate,
      }),
    });

    bindEvents(this.connection, peerConnectionEventMap);

    if (audioStream) {
      this.setLocalAudioStream(audioStream);
    }

    if (videoStream) {
      this.setLocalVideoStream(videoStream);
    }

    if (screenStream) {
      this.setLocalScreenStream(screenStream);
    }

    // Propagate initial connection state
    this.connection.getConnectionState().then(state => this.onConnectionStateChange(state));
  }

  async setLocalAudioStream(stream) {
    await this.connection.setLocalAudioStream(stream);
  }

  async setLocalVideoStream(stream) {
    await this.connection.setLocalVideoStream(stream);
  }

  async setLocalScreenStream(stream) {
    await this.connection.setLocalScreenStream(stream);
  }

  dumpCurrentSdps() {
    return this.connection.dumpCurrentSdps?.() ?? Promise.resolve({});
  }

  resetConnection() {
    this.connection.reset();
  }

  close() {
    try {
      this.canceller.cancel();
      this.connection.close();
    } catch (error) {
      Logger.error(error);
      FluentdLogger.warn({ name: 'Peer.close', error });
    }
  }

  sendMessage(type, content) {
    return this.room.sendMessage(type, this.id, content);
  }

  sendOffer({ offer, reset, mids }) {
    return this.sendMessage(SIGNALING_PROTOCOL.negotiation.sdpOffer, { offer, reset, mids });
  }

  sendNegotiationNeeded({ reset = false, mids } = {}) {
    return this.sendMessage(SIGNALING_PROTOCOL.negotiation.needed, { reset, mids });
  }

  sendIceCandidate({ candidate }) {
    return this.sendMessage(SIGNALING_PROTOCOL.negotiation.iceCandidate, { candidate });
  }

  async sendMediaStatus({ audio, video, speaking }) {
    const { cancelToken } = this.canceller;

    try {
      await this.sendMessage(SIGNALING_PROTOCOL.updates.mediaStatus, {
        audio,
        video,
        speaking,
        // Override info about channels if they're disabled due to codec incompatibility
        ...!this.channels.audio && { audio: TrackState.Off },
        ...!this.channels.video && { video: TrackState.Off },
      });
    } catch (error) {
      // Ignore media status updates failures after this Peer was
      // removed. `sendMediaStatus` is kind of special because
      // it's called from `Room` instead of being called from
      // `PeerConnection`'s `TaskQueue` (which takes care of
      // cancellation), like other `send*` methods.
      if (!cancelToken.isCancelled()) {
        Logger.error(error);
        FluentdLogger.warn({
          name: 'Peer.sendMediaStatus',
        });
      }
    }
  }

  dispatch(data) {
    const { type } = data;
    if (type in this.signalingEventMap) {
      return this.signalingEventMap[type](data);
    } else if (this.connection) {
      return this.connection.dispatch(data);
    }
    return null;
  }

  receiveBotStateUpdated({ botState }) {
    this.room.emit(RTC_ROOM_EVENTS.botStateChange, this.id, botState);
  }

  receiveMediaStatus({ audio, video, speaking }) {
    this.room.emit(RTC_ROOM_EVENTS.peerStreamStatusChange, this.id, {
      audio,
      video,
      speaking,
    });
  }

  onConnectionStateChange(state) {
    this.room.emit(RTC_ROOM_EVENTS.peerConnectionStateChange, this.id, state);
  }

  onConnectionError(error) {
    this.room.emit(RTC_ROOM_EVENTS.peerConnectionError, this.id, error);
  }

  onConnectionTimeout(connectionTimeout) {
    this.room.emit(RTC_ROOM_EVENTS.peerConnectionTimeout, this.id, connectionTimeout);
  }

  onRemoteAudioStream(stream) {
    this.room.emit(RTC_ROOM_EVENTS.peerAudioStreamChange, this.id, stream);
  }

  onRemoteVideoStream(stream) {
    this.room.emit(RTC_ROOM_EVENTS.peerVideoStreamChange, this.id, stream);
  }

  onRemoteScreenStream(stream) {
    this.room.emit(RTC_ROOM_EVENTS.peerScreenStreamChange, this.id, stream);
  }
}
